/*
============================================================================
SOGNO CUSTOM — AFFIRMATION PRODUCTS
File: assets/sogno-affirmation-products.js
Project: Sogno87

Features:
- FoxTheme / Swiper carousel
- Autoplay desktop + mobile
- Horizontal trackpad support
- Native mobile swipe
- Mobile 1 card + next-card preview
- Desktop navigation
- Direct variant add-to-cart from size circles
- Refresh/open native Sleek cart drawer
============================================================================
*/

(() => {
  'use strict';


  if (
    !customElements.get(
      'sogno-affirmation-products'
    )
  ) {

    customElements.define(
      'sogno-affirmation-products',

      class SognoAffirmationProducts extends HTMLElement {

        constructor() {
          super();

          this.carousel =
            null;

          this.slider =
            null;

          this.section =
            null;

          this.sectionId =
            null;

          this.swiperElement =
            null;

          this.gap =
            14;


          /* ================================================================
             AUTOPLAY
             ================================================================ */

          this.autoDelay =
            2800;

          this.autoTimer =
            null;

          this.autoPaused =
            false;


          /* ================================================================
             TRACKPAD
             ================================================================ */

          this.wheelLocked =
            false;

          this.wheelTimer =
            null;


          /* ================================================================
             BIND
             ================================================================ */

          this.handleClick =
            this.handleClick.bind(
              this
            );

          this.handleWheel =
            this.handleWheel.bind(
              this
            );

          this.handlePointerEnter =
            this.handlePointerEnter.bind(
              this
            );

          this.handlePointerLeave =
            this.handlePointerLeave.bind(
              this
            );

          this.handleTouchStart =
            this.handleTouchStart.bind(
              this
            );

          this.handleTouchEnd =
            this.handleTouchEnd.bind(
              this
            );

          this.handleVisibility =
            this.handleVisibility.bind(
              this
            );

        }


        /* ==================================================================
           CONNECT
           ================================================================== */

        connectedCallback() {

          if (
            this.dataset.sognoReady ===
            'true'
          ) {
            return;
          }


          this.dataset.sognoReady =
            'true';


          this.sectionId =
            this.dataset.sectionId;


          this.section =
            this.closest(
              `#SognoAffirmationProducts-${this.sectionId}`
            );


          this.gap =
            parseInt(
              this.dataset.gap ||
              '14',
              10
            );


          this.addEventListener(
            'click',
            this.handleClick
          );


          document.addEventListener(
            'visibilitychange',
            this.handleVisibility
          );


          this.initCarousel();

        }


        /* ==================================================================
           DISCONNECT
           ================================================================== */

        disconnectedCallback() {

          this.stopAutoplay();


          window.clearTimeout(
            this.wheelTimer
          );


          this.removeEventListener(
            'click',
            this.handleClick
          );


          document.removeEventListener(
            'visibilitychange',
            this.handleVisibility
          );


          if (
            this.swiperElement
          ) {

            this.swiperElement.removeEventListener(
              'wheel',
              this.handleWheel
            );


            this.swiperElement.removeEventListener(
              'pointerenter',
              this.handlePointerEnter
            );


            this.swiperElement.removeEventListener(
              'pointerleave',
              this.handlePointerLeave
            );


            this.swiperElement.removeEventListener(
              'touchstart',
              this.handleTouchStart
            );


            this.swiperElement.removeEventListener(
              'touchend',
              this.handleTouchEnd
            );

          }


          if (
            this.carousel &&
            this.carousel.slider &&
            typeof this.carousel.slider.destroy ===
            'function'
          ) {

            try {

              this.carousel
                .slider
                .destroy(
                  true,
                  true
                );

            } catch (error) {}

          }


          this.carousel =
            null;

          this.slider =
            null;

          this.swiperElement =
            null;

        }


        /* ==================================================================
           CAROUSEL
           ================================================================== */

        initCarousel() {

          if (
            !this.section ||
            !window.FoxTheme ||
            !window.FoxTheme.Carousel
          ) {
            return;
          }


          this.swiperElement =
            this.querySelector(
              '.swiper'
            );


          if (
            !this.swiperElement
          ) {
            return;
          }


          const nextButton =
            this.section.querySelector(
              '.sogno-aff-products__arrow--next'
            );


          const previousButton =
            this.section.querySelector(
              '.sogno-aff-products__arrow--prev'
            );


          const options = {

            /*
            MOBILE:
            1 tarjeta completa + parte de la siguiente.
            */

            slidesPerView:
              1.18,

            spaceBetween:
              Math.max(
                10,
                this.gap
              ),

            speed:
              520,

            threshold:
              4,

            loop:
              false,

            watchOverflow:
              true,

            autoHeight:
              false,

            grabCursor:
              true,

            resistanceRatio:
              0.72,

            navigation: {
              nextEl:
                nextButton,

              prevEl:
                previousButton
            },


            breakpoints: {

              560: {
                slidesPerView:
                  1.65,

                spaceBetween:
                  this.gap
              },


              768: {
                slidesPerView:
                  3,

                spaceBetween:
                  this.gap
              },


              1024: {
                slidesPerView:
                  4,

                spaceBetween:
                  this.gap
              },


              1500: {
                slidesPerView:
                  4.5,

                spaceBetween:
                  this.gap
              }

            }

          };


          this.carousel =
            new window.FoxTheme.Carousel(
              this.swiperElement,
              options
            );


          this.carousel.init();


          this.slider =
            this.carousel.slider;


          /*
          TRACKPAD DESKTOP.
          */

          this.swiperElement.addEventListener(
            'wheel',
            this.handleWheel,
            {
              passive:
                false
            }
          );


          /*
          AUTOPLAY PAUSE / RESUME.
          */

          this.swiperElement.addEventListener(
            'pointerenter',
            this.handlePointerEnter
          );


          this.swiperElement.addEventListener(
            'pointerleave',
            this.handlePointerLeave
          );


          this.swiperElement.addEventListener(
            'touchstart',
            this.handleTouchStart,
            {
              passive:
                true
            }
          );


          this.swiperElement.addEventListener(
            'touchend',
            this.handleTouchEnd,
            {
              passive:
                true
            }
          );


          /*
          ACCESSIBILITY.
          */

          if (
            window.FoxTheme.a11y &&
            typeof window.FoxTheme.a11y
              .getFocusableElements ===
              'function'
          ) {

            const focusableElements =
              window.FoxTheme.a11y
                .getFocusableElements(
                  this
                );


            focusableElements.forEach(
              (element) => {

                element.addEventListener(
                  'focusin',
                  () => {

                    const slide =
                      element.closest(
                        '.swiper-slide'
                      );


                    if (
                      !slide ||
                      !this.slider
                    ) {
                      return;
                    }


                    const index =
                      Array.from(
                        this.slider.slides
                      ).indexOf(
                        slide
                      );


                    if (
                      index >=
                      0
                    ) {

                      this.slider.slideTo(
                        index
                      );

                    }

                  }
                );

              }
            );

          }


          /*
          START AUTOMATIC MOVEMENT.
          */

          this.startAutoplay(
            1400
          );

        }


        /* ==================================================================
           AUTOPLAY
           ================================================================== */

        startAutoplay(
          initialDelay =
            this.autoDelay
        ) {

          this.stopAutoplay();


          if (
            !this.slider ||
            document.hidden
          ) {
            return;
          }


          this.autoTimer =
            window.setTimeout(
              () => {

                this.runAutoplay();

              },
              initialDelay
            );

        }


        stopAutoplay() {

          if (
            this.autoTimer
          ) {

            window.clearTimeout(
              this.autoTimer
            );


            this.autoTimer =
              null;

          }

        }


        runAutoplay() {

          if (
            !this.slider ||
            this.autoPaused ||
            document.hidden
          ) {

            this.startAutoplay();

            return;

          }


          /*
          If we reached the last product,
          return smoothly to the beginning.
          */

          if (
            this.slider.isEnd
          ) {

            this.slider.slideTo(
              0,
              620
            );

          } else {

            this.slider.slideNext(
              620
            );

          }


          this.startAutoplay();

        }


        restartAutoplay() {

          this.stopAutoplay();


          this.autoPaused =
            false;


          this.startAutoplay(
            this.autoDelay
          );

        }


        handlePointerEnter(
          event
        ) {

          /*
          Only desktop mouse/trackpad hover.
          Touch devices ignore this behavior.
          */

          if (
            event.pointerType &&
            event.pointerType !==
            'mouse'
          ) {
            return;
          }


          this.autoPaused =
            true;


          this.stopAutoplay();

        }


        handlePointerLeave() {

          this.restartAutoplay();

        }


        handleTouchStart() {

          this.autoPaused =
            true;


          this.stopAutoplay();

        }


        handleTouchEnd() {

          window.setTimeout(
            () => {

              this.restartAutoplay();

            },
            900
          );

        }


        handleVisibility() {

          if (
            document.hidden
          ) {

            this.stopAutoplay();

          } else {

            this.startAutoplay(
              900
            );

          }

        }


        /* ==================================================================
           TRACKPAD / HORIZONTAL WHEEL
           ================================================================== */

        handleWheel(
          event
        ) {

          if (
            !this.slider
          ) {
            return;
          }


          const horizontal =
            Math.abs(
              event.deltaX
            );


          const vertical =
            Math.abs(
              event.deltaY
            );


          /*
          Only react when the gesture is clearly horizontal.

          Vertical page scrolling continues normally.
          */

          if (
            horizontal < 8 ||
            horizontal <=
            vertical
          ) {
            return;
          }


          event.preventDefault();


          if (
            this.wheelLocked
          ) {
            return;
          }


          this.wheelLocked =
            true;


          this.stopAutoplay();


          if (
            event.deltaX >
            0
          ) {

            if (
              this.slider.isEnd
            ) {

              this.slider.slideTo(
                0,
                500
              );

            } else {

              this.slider.slideNext(
                500
              );

            }

          } else {

            if (
              this.slider.isBeginning
            ) {

              const lastIndex =
                Math.max(
                  0,
                  this.slider.slides.length -
                  1
                );


              this.slider.slideTo(
                lastIndex,
                500
              );

            } else {

              this.slider.slidePrev(
                500
              );

            }

          }


          window.clearTimeout(
            this.wheelTimer
          );


          this.wheelTimer =
            window.setTimeout(
              () => {

                this.wheelLocked =
                  false;


                this.startAutoplay(
                  this.autoDelay
                );

              },
              520
            );

        }


        /* ==================================================================
           CLICK
           ================================================================== */

        handleClick(
          event
        ) {

          const sizeButton =
            event.target.closest(
              '[data-sogno-size-button]'
            );


          if (
            !sizeButton
          ) {
            return;
          }


          event.preventDefault();

          event.stopPropagation();


          if (
            sizeButton.disabled ||
            sizeButton.getAttribute(
              'aria-disabled'
            ) ===
            'true' ||
            sizeButton.classList.contains(
              'is-loading'
            )
          ) {
            return;
          }


          const variantId =
            sizeButton.dataset.variantId;


          if (
            !variantId
          ) {
            return;
          }


          this.stopAutoplay();


          this.addVariantToCart(
            sizeButton,
            variantId
          );

        }


        /* ==================================================================
           ADD VARIANT
           ================================================================== */

        async addVariantToCart(
          button,
          variantId
        ) {

          const productCard =
            button.closest(
              '[data-sogno-product-card]'
            );


          const message =
            productCard
              ?.querySelector(
                '[data-sogno-product-message]'
              );


          button.classList.add(
            'is-loading'
          );


          button.setAttribute(
            'aria-busy',
            'true'
          );


          if (
            message
          ) {

            message.textContent =
              '';

          }


          try {

            const cartAddUrl =
              window.FoxTheme
                ?.routes
                ?.cart_add_url ||
              `${window.Shopify?.routes?.root || '/'}cart/add.js`;


            const response =
              await fetch(
                cartAddUrl,
                {
                  method:
                    'POST',

                  credentials:
                    'same-origin',

                  headers: {
                    'Content-Type':
                      'application/json',

                    'Accept':
                      'application/json',

                    'X-Requested-With':
                      'XMLHttpRequest'
                  },

                  body:
                    JSON.stringify(
                      {
                        items: [
                          {
                            id:
                              Number(
                                variantId
                              ),

                            quantity:
                              1
                          }
                        ]
                      }
                    )
                }
              );


            const result =
              await response.json();


            if (
              !response.ok
            ) {

              throw new Error(
                result.description ||
                result.message ||
                'No pudimos agregar esta talla.'
              );

            }


            button.classList.remove(
              'is-loading'
            );


            button.classList.add(
              'is-added'
            );


            button.removeAttribute(
              'aria-busy'
            );


            if (
              message
            ) {

              message.textContent =
                'Agregado al carrito';

            }


            document.dispatchEvent(
              new CustomEvent(
                'cart:refresh',
                {
                  detail: {
                    open:
                      true
                  }
                }
              )
            );


            window.setTimeout(
              () => {

                button.classList.remove(
                  'is-added'
                );


                if (
                  message
                ) {

                  message.textContent =
                    '';

                }


                this.startAutoplay(
                  900
                );

              },
              1400
            );

          } catch (
            error
          ) {

            button.classList.remove(
              'is-loading'
            );


            button.removeAttribute(
              'aria-busy'
            );


            if (
              message
            ) {

              message.textContent =
                error.message ||
                'No pudimos agregar esta talla.';

            }


            this.startAutoplay(
              this.autoDelay
            );


            console.error(
              'Sogno add to cart:',
              error
            );

          }

        }

      }

    );

  }

})();